import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Truck, Info, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { API_URL } from '@/lib/api';

const DeliverySettings = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [settings, setSettings] = useState({
    deliveryCharge: 50,
    freeDeliveryAbove: 0,
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings`);
        setSettings({
          deliveryCharge: res.data.deliveryCharge ?? 50,
          freeDeliveryAbove: res.data.freeDeliveryAbove ?? 0,
        });
      } catch (error) {
        toast.error('Failed to load settings');
      } finally {
        setFetching(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.patch(`${API_URL}/settings`, {
        deliveryCharge: Number(settings.deliveryCharge),
        freeDeliveryAbove: Number(settings.freeDeliveryAbove),
      });
      toast.success('Delivery settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div data-testid="delivery-settings-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Delivery Settings</h1>
          <p className="text-slate-500 mt-1">Configure global delivery rules for all stores</p>
        </div>
      </div>

      <div className="max-w-2xl space-y-6">
        <Card className="rounded-2xl border-slate-100 shadow-card">
          <CardHeader className="pb-3 text-center lg:text-left">
            <div className="w-12 h-12 rounded-2xl bg-purple-50 flex items-center justify-center mb-4 mx-auto lg:mx-0">
              <Truck className="w-6 h-6 text-purple-600" />
            </div>
            <CardTitle className="text-xl font-heading">Global Delivery Charges</CardTitle>
            <CardDescription>
              These rates will be applied to every order placed by a store.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="deliveryCharge" className="text-sm font-semibold">Standard Delivery Charge (₹)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">₹</span>
                    <Input
                      id="deliveryCharge"
                      type="number"
                      min="0"
                      step="1"
                      className="pl-8 rounded-xl border-slate-200 focus:border-purple-400 focus:ring-purple-400"
                      value={settings.deliveryCharge}
                      onChange={(e) => setSettings(prev => ({ ...prev, deliveryCharge: e.target.value }))}
                      required
                    />
                  </div>
                  <p className="text-[11px] text-slate-400">Flat rate applied if threshold is not met.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="freeDeliveryAbove" className="text-sm font-semibold">Free Delivery Above (₹)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">₹</span>
                    <Input
                      id="freeDeliveryAbove"
                      type="number"
                      min="0"
                      step="1"
                      className="pl-8 rounded-xl border-slate-200 focus:border-purple-400 focus:ring-purple-400"
                      value={settings.freeDeliveryAbove}
                      onChange={(e) => setSettings(prev => ({ ...prev, freeDeliveryAbove: e.target.value }))}
                      required
                    />
                  </div>
                  <p className="text-[11px] text-slate-400">Set to 0 to always charge delivery fee.</p>
                </div>
              </div>

              <Separator className="bg-slate-50" />

              <div className="bg-blue-50/50 rounded-xl p-4 flex gap-3 border border-blue-100/50">
                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700 leading-relaxed">
                  <p className="font-semibold mb-1">How it works:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>If Order Amount ≥ Free Delivery Threshold → <strong>₹0 Delivery</strong></li>
                    <li>If Order Amount &lt; Free Delivery Threshold → <strong>₹{settings.deliveryCharge} Delivery</strong></li>
                  </ul>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading} 
                className="w-full lg:w-auto px-8 rounded-xl h-11 text-sm font-bold shadow-lg shadow-purple-200 transition-all hover:shadow-purple-300"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}
              >
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving Changes…</> : <><CheckCircle2 className="w-4 h-4 mr-2" />Save Delivery Rules</>}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Informational Card */}
        <Card className="rounded-2xl border-slate-100 bg-slate-50/50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-white border border-slate-100 shadow-sm">
                <Info className="w-4 h-4 text-slate-400" />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-700">Audit Log</p>
                <p className="text-[10px] text-slate-500 mt-0.5">Delivery settings changes are applied instantly and will reflect on all new orders placed by stores.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DeliverySettings;
